/**
 * Magic Fill — AI backfill for empty Global Words fields via DeepSeek gateway.
 */
import { getWordSubtypes } from './wordTaxonomy.js';

export const MAGIC_FILL_FIELDS = [
    { key: 'russian', label: 'Russian Translation' },
    { key: 'ipa', label: 'IPA Transcription' },
    { key: 'regional_accent', label: 'Regional Accent' },
    { key: 'spelling_variation', label: 'Spelling Variation' },
    { key: 'collocations', label: 'Collocations' },
    { key: 'example', label: 'Example sentence' },
    { key: 'definition', label: 'Definition' },
    { key: 'subtypes', label: 'Subtypes' },
    { key: 'root', label: 'Root word' },
    { key: 'confused_with', label: 'Confused with' },
];

export const MAGIC_FILL_FIELD_KEYS = MAGIC_FILL_FIELDS.map((f) => f.key);

/** Subtype options from DatabaseFilter (filter view). */
export const MAGIC_FILL_SUBTYPES = [
    'verb',
    'noun',
    'adjective',
    'preposition',
    'phrase',
    'adverb',
    'phrasal verb',
    'idiom',
    'number',
];

export const MAGIC_FILL_BATCH_SIZE = 10;
export const MAGIC_FILL_CONCURRENCY = 2;

const CTX_KEYS = ['russian', 'subtypes', 'subtype', 'course', 'level', 'theme', 'module', 'lesson'];

export const MAGIC_FILL_SYSTEM_PROMPT = `You are a lexicographer for an English–Russian classroom dictionary.
Given English headwords and optional existing fields, fill ONLY the requested empty keys for each item.

Output rules:
- Return a single JSON array only. No markdown fences, no commentary, no extra keys beyond id plus the requested fill keys.
- Each array element must include "id" matching the input item id, plus ONLY the keys listed in that item's "fill" array.
- Use "" when a requested field should be empty (e.g. root when the headword is already the base; regional_accent when none).
- Headword stays a lemma: no article (a/an/the), no leading "to" on verbs.
- British English spelling by default unless regional_accent or spelling_variation says otherwise.

Field rules (apply only when that key is requested):

IPA (ipa):
- Output IPA inside square brackets, e.g. [əˈɡriː]
- Citation form only; mark stress with ˈ / ˌ
- Default: British RP unless regional_accent is AmE

Regional Accent (regional_accent):
- Exactly one of: AmE | BrE | (empty string)
- Use AmE or BrE only when the pronunciation/spelling variety matters for this headword
- If no useful distinction, return ""

Subtypes (subtypes):
- Return a JSON array containing one or more values from this list:
  verb, noun, adjective, preposition, phrase, adverb, phrasal verb, idiom, number
- Infer primarily from the Russian gloss when present:
  - verbal gloss (любить, соглашаться) → verb
  - nominal gloss (любовь, сделка) → noun
  - adjectival → adjective; etc.
- Prefer phrasal verb / idiom / phrase when the English unit is multi-word
- Include every genuinely applicable value (for example ["phrase","verb"]); do not add weak guesses
- If English and Russian conflict, trust Russian for POS

Root word (root):
- The single English base form from which the headword is formed by prefix, suffix, or other morphological change
- Examples: agreement → agree; unhappy → happy; decision → decide
- If the headword is already the base (or an opaque idiom with no useful root), return ""

Example (example):
- Max ~10 words in the English sentence
- Exact pattern:
  <English sentence with [HEADWORD_FORM/RU_FORM]>. // <Russian sentence with [RU_FORM]>.
- English side: put the headword (inflected as used) + slash + its Russian counterpart from the Russian sentence, all inside one pair of square brackets
- Russian side: put that same Russian word/form in square brackets (REQUIRED)
- Both sentences end with a period
- The English gap form must match the requested/inferred subtype (verb form for verb, noun form for noun, etc.)
- Example: They [love/любят] music. // Они [любят] музыку.

Definition (definition):
- Define the FULL English headword/phrase — not a sub-part of a multi-word unit
- English only; short learner definition
- Cap CEFR of the definition language using level context on the item:
  L1, L2 → A1; L3, L4 → A2; L5 → B1; L6, L7 → B2
- If no level context, keep language ≤ B1

Russian (russian):
- Natural dictionary gloss; verbs as infinitive when possible; nouns nominative
- Sense must match subtype and any theme/example context if present

Collocations (collocations):
- Format only: chunk[RU]//chunk[RU]//… (2–5 items)

Spelling Variation (spelling_variation):
- Alternate spelling only (AmE↔BrE), else ""

Confused With (confused_with):
- 1–3 comma-separated confusable items, else ""

Never fill or invent: comment, dateAdded, level, difficulty, difficulty_type, calculated_difficulty, type, theme, subtheme, word_family, id (id is echo-only).`;

export function isEmptyField(value) {
    if (value == null) return true;
    if (Array.isArray(value)) return value.length === 0;
    return String(value).trim() === '';
}

export function mapLevelToCefr(levelOrCourse) {
    const s = String(levelOrCourse || '').trim();
    if (!s) return '';
    const cefr = s.match(/\b(A1|A2|B1|B2|C1|C2)\b/i);
    if (cefr) return cefr[1].toUpperCase();
    const l = s.match(/L\s*([1-7])\b/i);
    if (l) {
        const n = Number(l[1]);
        if (n <= 2) return 'A1';
        if (n <= 4) return 'A2';
        if (n === 5) return 'B1';
        return 'B2';
    }
    return '';
}

export function buildMagicFillCtx(word) {
    const ctx = {};
    for (const key of CTX_KEYS) {
        if (!isEmptyField(word[key])) ctx[key] = word[key];
    }
    const cefr = mapLevelToCefr(word.level) || mapLevelToCefr(word.course);
    if (cefr) ctx.cefr_cap = cefr;
    return ctx;
}

export function getEmptyFillKeys(word, selectedFieldKeys = MAGIC_FILL_FIELD_KEYS) {
    return selectedFieldKeys.filter((key) => (
        key === 'subtypes'
            ? getWordSubtypes(word).length === 0
            : isEmptyField(word[key])
    ));
}

export function buildMagicFillBatchItems(words, selectedFieldKeys = MAGIC_FILL_FIELD_KEYS) {
    return words
        .map((word) => {
            const fill = getEmptyFillKeys(word, selectedFieldKeys);
            if (!fill.length || isEmptyField(word.english)) return null;
            return {
                id: word.id,
                english: String(word.english).trim(),
                ctx: buildMagicFillCtx(word),
                fill,
            };
        })
        .filter(Boolean);
}

export function buildMagicFillUserPrompt(items) {
    return `Fill empty fields for each item. Return a JSON array only.
Each element: {"id":"<uuid>","english":"..."} plus ONLY the keys listed in "fill".
Do not invent keys. Use "" when a requested field should be empty (e.g. root when headword is base, regional_accent when none).

Items:
${JSON.stringify(items)}`;
}

export function chunkArray(arr, size) {
    const out = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
}

export function parseMagicFillJsonArray(raw) {
    if (raw == null) throw new Error('Empty AI response');
    let text = String(raw).trim();
    if (text.startsWith('```')) {
        text = text.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/, '').trim();
    }
    const start = text.indexOf('[');
    const end = text.lastIndexOf(']');
    if (start === -1 || end === -1 || end <= start) {
        throw new Error('AI response is not a JSON array');
    }
    const parsed = JSON.parse(text.slice(start, end + 1));
    if (!Array.isArray(parsed)) throw new Error('AI response is not a JSON array');
    return parsed;
}

function normalizeIpa(value) {
    let s = String(value ?? '').trim();
    if (!s) return '';
    s = s.replace(/^\/+|\/+$/g, '').trim();
    if (s.startsWith('[') && s.endsWith(']')) return s;
    return `[${s.replace(/^\[+|\]+$/g, '')}]`;
}

function normalizeRegionalAccent(value) {
    const s = String(value ?? '').trim();
    if (!s) return '';
    if (/^ame$/i.test(s) || /^american/i.test(s)) return 'AmE';
    if (/^bre$/i.test(s) || /^british/i.test(s) || /^rp$/i.test(s)) return 'BrE';
    if (s === 'AmE' || s === 'BrE') return s;
    return '';
}

function normalizeSubtypes(value) {
    const values = Array.isArray(value) ? value : String(value ?? '').split(/[,;]/);
    const out = [];
    values.forEach((item) => {
        let s = String(item ?? '').trim().toLowerCase();
        if (s === 'prep') s = 'preposition';
        const match = MAGIC_FILL_SUBTYPES.find((opt) => opt === s || opt.replace(/\s+/g, '') === s.replace(/\s+/g, ''));
        if (match && !out.includes(match)) out.push(match);
    });
    return out;
}

function normalizeRoot(value, english) {
    const root = String(value ?? '').trim();
    if (!root) return '';
    const en = String(english ?? '').trim().toLowerCase();
    if (root.toLowerCase() === en) return '';
    return root;
}

/**
 * Light validation / normalization of one AI result object.
 * Drops unknown keys and values that fail hard checks (example format).
 */
export function normalizeMagicFillResult(rawItem, expectedFill, english) {
    if (!rawItem || typeof rawItem !== 'object') return {};
    const out = {};
    for (const key of expectedFill) {
        if (!(key in rawItem)) continue;
        let value = rawItem[key];
        if (value == null) value = key === 'subtypes' ? [] : '';
        if (key !== 'subtypes' && typeof value !== 'string') value = String(value);

        if (key === 'ipa') {
            value = normalizeIpa(value);
        } else if (key === 'regional_accent') {
            value = normalizeRegionalAccent(value);
        } else if (key === 'subtypes') {
            value = normalizeSubtypes(value);
            if (!value.length) continue;
        } else if (key === 'root') {
            value = normalizeRoot(value, english);
        } else if (key === 'example') {
            value = value.trim();
            if (!value.includes('//') || !value.includes('[')) continue;
            // Require brackets on Russian half when possible
            const parts = value.split('//');
            if (parts.length >= 2 && !parts[1].includes('[')) continue;
        } else {
            value = value.trim();
        }

        out[key] = value;
    }
    return out;
}

/**
 * Run fill batches with limited concurrency.
 * onProgress({ doneBatches, totalBatches, lastError })
 */
export async function runMagicFillBatches(items, fillBatchFn, {
    batchSize = MAGIC_FILL_BATCH_SIZE,
    concurrency = MAGIC_FILL_CONCURRENCY,
    onProgress,
    signal,
} = {}) {
    const batches = chunkArray(items, batchSize);
    const resultsById = new Map();
    const errors = [];
    let doneBatches = 0;

    const report = (lastError) => {
        if (onProgress) {
            onProgress({
                doneBatches,
                totalBatches: batches.length,
                filledCount: resultsById.size,
                lastError,
            });
        }
    };

    report(null);

    let nextIndex = 0;
    async function worker() {
        while (nextIndex < batches.length) {
            if (signal?.aborted) throw new Error('Cancelled');
            const i = nextIndex;
            nextIndex += 1;
            const batch = batches[i];
            try {
                const raw = await fillBatchFn(batch);
                const parsed = parseMagicFillJsonArray(raw);
                const byId = new Map(parsed.map((row) => [String(row.id), row]));
                for (const item of batch) {
                    const row = byId.get(String(item.id));
                    const english = item.english;
                    const normalized = normalizeMagicFillResult(row, item.fill, english);
                    if (Object.keys(normalized).length > 0) {
                        resultsById.set(String(item.id), {
                            id: item.id,
                            english,
                            fill: item.fill,
                            values: normalized,
                        });
                    }
                }
            } catch (err) {
                errors.push({ batchIndex: i, message: err.message || String(err) });
                report(err.message || String(err));
            } finally {
                doneBatches += 1;
                report(null);
            }
        }
    }

    const workers = Array.from({ length: Math.min(concurrency, batches.length || 1) }, () => worker());
    await Promise.all(workers);

    return {
        results: [...resultsById.values()],
        errors,
        totalBatches: batches.length,
    };
}
